import { Component, OnDestroy, OnInit } from '@angular/core';
import { HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';
import 'rxjs/add/operator/takeUntil';
import { Group } from '../../../models/group.model';
import { GroupUser } from '../../../models/group-user.model';
import { DepartmentReference, GroupContext } from '../../../models/group-context.model';
import { User } from '../../../models/user.model';
import { GROUP_CONSTANTS, HTTP_STATUS_CODES } from '../../../shared.constants/all-constants';
import {
	VIEW_GROUP_BOOLEAN_TYPE,
	VIEW_GROUP_FIRST_INDEX,
	VIEW_GROUP_EMPTY_TEXT,
	VIEW_GROUP_NATIVE_NAME_PLACEHOLDERS,
	VIEW_GROUP_FIRST_DISPLAY_INDEX,
	VIEW_GROUP_MISSING_INDEX,
	VIEW_GROUP_RELOAD_TARGETS
} from './view-group.constants';
import {
	SortCriterion,
	ViewGroupMemberDeletionRequest,
	ViewGroupMemberActionRequest,
	VIEW_GROUP_MEMBER_ACTIONS,
	ViewGroupReloadTarget,
	ViewGroupUiStateSnapshot
} from './view-group.types';
import { ViewGroupNotificationPort } from './view-group-notification.port';
import { ViewGroupPayrollCurrencyPort } from './view-group-payroll-currency.port';
import { ViewGroupPlatform } from './view-group.platform';
import { ViewGroupRosterCoordinator } from './view-group-roster.coordinator';
import { ViewGroupSettingsApi } from './view-group-settings.api';
import { ViewGroupSettingsCoordinator } from './view-group-settings.coordinator';
import { ViewGroupDataPort } from './view-group-data.port';
import { ViewGroupIdentityState } from './view-group-identity.state';
import { ViewGroupUiCoordinator } from './view-group-ui.coordinator';

@Component({
	selector: 'app-view-group',
	templateUrl: './view-group.component.html',
	styleUrls: ['./view-group.component.css'],
	providers: [
		ViewGroupDataPort,
		ViewGroupIdentityState,
		ViewGroupPlatform,
		ViewGroupUiCoordinator,
		ViewGroupPayrollCurrencyPort,
		ViewGroupNotificationPort,
		ViewGroupSettingsApi,
		ViewGroupSettingsCoordinator,
		ViewGroupRosterCoordinator
	]
})
export class ViewGroupComponent implements OnInit, OnDestroy {
	user: User;
	group: Group;
	groupContext: GroupContext = null;
	contextLoading = false;
	contextError = false;
	groups: Group[] = [];
	mainGroup: string;
	id: string;
	mobWidth: number;
	rep: boolean;
	isAdmin = false;
	isHeadOfGroup = false;
	isUser = false;
	isHead = false;
	isDeputy = false;
	isLoaded = false;
	errorInfo = false;
	dataListVector: Object[] = [];
	suc = false;
	nodata = false;
	private readonly destroy$ = new Subject<void>();
	private readonly groupChanged$ = new Subject<void>();
	private readonly contextChanged$ = new Subject<void>();
	private requestedGroupId: string | number;

	constructor(
		readonly roster: ViewGroupRosterCoordinator,
		readonly ui: ViewGroupUiCoordinator,
		readonly settings: ViewGroupSettingsCoordinator
	) {
		const localUser = this.roster.currentUser();
		this.id = localUser.id;
		this.rep = this.roster.isReportMode();
		this.bindCoordinatorEvents();
		this.ui.start(this.contextChanged$, this.destroy$);
	}

	ngOnInit(): void {
		this.user = this.roster.currentUser();
		this.initializeRoles();
		this.roster.routeGroupIds().takeUntil(this.destroy$).subscribe(groupId => this.getList(groupId));
		this.loadAvailableGroups();
	}

	ngOnDestroy(): void {
		this.clearGroupContext();
		this.groupChanged$.next();
		this.groupChanged$.complete();
		this.destroy$.next();
		this.destroy$.complete();
		this.contextChanged$.complete();
	}

	nativeName(value: string): string {
		const normalized = (value || VIEW_GROUP_EMPTY_TEXT).trim().toLowerCase();
		return normalized && VIEW_GROUP_NATIVE_NAME_PLACEHOLDERS.indexOf(normalized) < VIEW_GROUP_FIRST_INDEX ? value : null;
	}

	get canManagePersonalStandards(): boolean {
		return this.hasCurrentGroupContext() && this.groupContext.canManagePersonalStandards;
	}

	get canSwitchGroup(): boolean {
		return !!this.group && this.groups.some(group => group.id !== this.group.id);
	}

	get isCurrentGroupListed(): boolean {
		return !!this.group && this.groups.some(group => group.id === this.group.id);
	}

	get canReadDepartmentAssets(): boolean {
		return this.hasCurrentGroupContext() && !this.group.parentDepartmentId &&
			!this.groupContext.parentDepartment && this.groupContext.canReadDepartmentAssets === true;
	}

	get canShowDepartmentActionBar(): boolean {
		return this.canShowMistakeListAction || this.canShowDepartmentToolActions ||
			this.canReadSalaryReport || this.canShowDealPayrollSettings || this.canManagePersonalStandards ||
			this.canManageClothingMarkers || this.canReadDepartmentAssets || this.canShowDepartmentStandardsAction ||
			this.settings.canShowMorningOvertime;
	}

	get canShowMistakeListAction(): boolean {
		return !!this.group && !!this.group.head && this.isHead &&
			!this.isHeadOfGroup && this.group.id !== this.mainGroup;
	}

	get canShowDepartmentToolActions(): boolean {
		return this.canManageGroupSettings;
	}

	get canShowDealPayrollSettings(): boolean {
		return this.hasCurrentGroupContext() && this.groupContext.canReadPayrollSettings;
	}

	get canShowDepartmentStandardsAction(): boolean {
		return this.isHead && this.isAdmin;
	}

	get canManageClothingMarkers(): boolean {
		return this.hasCurrentGroupContext() && this.groupContext.canManageClothingMarkers;
	}

	trackByDepartment(index: number, department: DepartmentReference): string {
		return department.id;
	}

	getAllGroups(): void {
		const request: Observable<HttpResponse<Group[]>> = this.roster.loadHeadGroups();
		request.takeUntil(this.destroy$).subscribe(
			response => this.handleHeadGroupsLoaded(response),
			() => undefined
		);
	}

	getAllGroupForList(): void {
		const request: Observable<HttpResponse<Group[]>> = this.roster.loadAllGroups();
		request.takeUntil(this.destroy$).subscribe(
			response => this.applyAvailableGroups(response.body),
			() => undefined
		);
	}

	getUsersByIdGroup(groupId: string | number): void {
		this.getList(groupId);
	}

	getList(groupId: string | number): void {
		this.requestedGroupId = groupId;
		this.beginGroupLoad();
		const request: Observable<HttpResponse<Group>> = this.roster.loadGroup(groupId);
		request.takeUntil(this.groupChanged$).takeUntil(this.destroy$).subscribe(
			response => this.handleGroupLoaded(response, String(groupId)),
			() => this.handleGroupLoadError()
		);
	}

	reloadCurrentGroup(): void {
		const groupId = this.requestedGroupId === undefined ? this.roster.routeGroupId() : this.requestedGroupId;
		this.getList(groupId);
	}

	private initializeRoles(): void {
		this.isAdmin = this.hasRole(GROUP_CONSTANTS.ROLE_ADMIN);
		this.isHead = this.hasRole(GROUP_CONSTANTS.ROLE_HEAD);
		this.isUser = this.hasRole(GROUP_CONSTANTS.ROLE_USER);
		this.syncIdentity();
	}

	private hasRole(role: string): boolean {
		return this.user.roles.indexOf(role) !== VIEW_GROUP_MISSING_INDEX;
	}

	private loadAvailableGroups(): void {
		if (this.isHead && this.isAdmin) {
			this.getAllGroupForList();
		} else if (this.isHead && !this.rep) {
			this.getAllGroups();
		} else if (this.isHead && this.rep) {
			this.getAllGroupForList();
		}
	}

	private handleHeadGroupsLoaded(response: HttpResponse<Group[]>): void {
		if (response.status === HTTP_STATUS_CODES.OK) {
			this.applyAvailableGroups(response.body);
		}
	}

	private applyAvailableGroups(groups: Group[]): void {
		this.mainGroup = this.roster.currentUser().mainGroup;
		this.groups = groups.slice();
		const mainGroupIndex = this.groups.findIndex(group => group.id === this.mainGroup);
		if (mainGroupIndex === VIEW_GROUP_MISSING_INDEX) {
			return;
		}
		const mainGroup = this.groups.splice(mainGroupIndex, VIEW_GROUP_FIRST_DISPLAY_INDEX)[VIEW_GROUP_FIRST_INDEX];
		this.groups.unshift(mainGroup);
	}

	get canManageGroupSettings(): boolean {
		return this.hasCurrentGroupContext() && this.groupContext.canManageGroupSettings;
	}

	get canAssignHead(): boolean {
		return this.hasCurrentGroupContext() && this.groupContext.canAssignHead;
	}

	get canManageMembership(): boolean {
		return this.hasCurrentGroupContext() && this.groupContext.canManageMembership;
	}

	get canReadSalaryReport(): boolean {
		return this.hasCurrentGroupContext() && this.groupContext.canReadSalaryReport;
	}

	trackReportUser(_index: number, member: GroupUser): string {
		return member.user.id;
	}

	private beginGroupLoad(): void {
		this.settings.cancelPayrollCurrency();
		this.groupChanged$.next();
		this.clearGroupContext();
		this.group = null;
		this.isLoaded = false;
		this.errorInfo = false;
		this.isHeadOfGroup = false;
		this.isDeputy = false;
		this.roster.resetVacationPresence();
		this.settings.resetMorningOvertime();
		this.syncIdentity();
	}

	private handleGroupLoaded(response: HttpResponse<Group>, groupId: string): void {
		if (response.status !== HTTP_STATUS_CODES.OK || !response.body || response.body.id !== groupId) {
			this.handleGroupLoadError();
			return;
		}
		this.applyLoadedGroup(response.body);
		this.roster.loadVacationPresence(groupId);
		this.loadVectors(this.roster.sortCriteria);
		this.loadGroupContext(groupId);
	}

	private applyLoadedGroup(group: Group): void {
		this.group = group;
		this.isLoaded = true;
		this.errorInfo = false;
		this.updateGroupRoles();
		this.syncIdentity();
		this.group.positions.forEach(position => position.isPosSelected = false);
	}

	private updateGroupRoles(): void {
		this.isHeadOfGroup = !!this.group.head && this.group.head.id === this.id;
		this.isDeputy = !!this.group.deputy && this.group.deputy.id === this.id;
	}

	private handleGroupLoadError(): void {
		this.isLoaded = true;
		this.errorInfo = true;
		this.roster.resetVacationPresence();
		this.settings.resetMorningOvertime();
	}

	private loadVectors(criteria: SortCriterion[]): void {
		this.roster.loadVectors(this.group.id, this.isAdmin, criteria);
	}

	private hasCurrentGroupContext(): boolean {
		return !!this.group && !!this.groupContext && this.group.id === this.groupContext.groupId;
	}

	private clearGroupContext(): void {
		this.contextChanged$.next();
		this.groupContext = null;
		this.contextLoading = false;
		this.contextError = false;
		this.syncIdentity();
	}

	private loadGroupContext(groupId: string): void {
		this.contextLoading = true;
		this.roster.loadGroupContext(groupId).takeUntil(this.groupChanged$).takeUntil(this.destroy$).subscribe(
			response => this.applyGroupContext(response, groupId),
			() => this.handleContextError()
		);
	}

	private applyGroupContext(response: HttpResponse<GroupContext>, groupId: string): void {
		if (!this.group || this.group.id !== groupId || response.status !== HTTP_STATUS_CODES.OK ||
			!response.body || response.body.groupId !== groupId || !this.hasContextCapabilities(response.body)) {
			this.handleContextError();
			return;
		}
		this.contextChanged$.next();
		this.groupContext = response.body;
		this.contextLoading = false;
		this.contextError = false;
		this.syncIdentity();
		this.settings.acceptPayrollCurrencyContext();
		this.settings.loadMorningOvertime();
	}

	private hasContextCapabilities(context: GroupContext): boolean {
		return Array.isArray(context.subdepartments) && [context.canManageGroupSettings, context.canManageMembership,
			context.canReadSalaryReport, context.canManagePersonalStandards, context.canManageClothingMarkers,
			context.canManageMorningOvertime, context.canReadPayrollSettings, context.canManagePayrollCurrency]
			.every(value => typeof value === VIEW_GROUP_BOOLEAN_TYPE);
	}

	private handleContextError(): void {
		this.clearGroupContext();
		this.contextError = true;
		this.settings.resetMorningOvertime();
	}

	private bindCoordinatorEvents(): void {
		this.ui.stateChanges.takeUntil(this.destroy$).subscribe(
			state => this.applyUiState(state)
		);
		this.ui.reloadRequests.takeUntil(this.destroy$).subscribe(
			target => this.handleReloadRequest(target)
		);
		this.ui.memberDeletionRequests.takeUntil(this.destroy$).subscribe(
			request => this.deleteMember(request)
		);
		this.ui.memberActionRequests.takeUntil(this.destroy$).subscribe(request => this.loadMemberActionContext(request));
	}

	private loadMemberActionContext(request: ViewGroupMemberActionRequest): void {
		this.roster.loadGroupContext(request.groupId).takeUntil(this.groupChanged$).takeUntil(this.destroy$).subscribe(response => {
			if (response.status !== HTTP_STATUS_CODES.OK || !response.body) { return; }
			this.completeMemberAction(request, response.body);
		}, () => undefined);
	}

	private completeMemberAction(request: ViewGroupMemberActionRequest, context: GroupContext): void {
		if (request.action !== VIEW_GROUP_MEMBER_ACTIONS.SHIFT) {
			this.ui.completeMemberAction(request, context);
			return;
		}
		if (!context.canManageGroupSettings || context.groupId !== request.groupId) { return; }
		this.roster.loadGroup(request.groupId).takeUntil(this.groupChanged$).takeUntil(this.destroy$).subscribe(response => {
			if (response.status === HTTP_STATUS_CODES.OK) { this.ui.completeMemberAction(request, context, response.body); }
		}, () => undefined);
	}

	private applyUiState(state: ViewGroupUiStateSnapshot): void {
		this.mobWidth = state.mobWidth;
		this.dataListVector = state.dataListVector;
		this.suc = state.suc;
		this.nodata = state.nodata;
	}

	private handleReloadRequest(target: ViewGroupReloadTarget): void {
		if (target === VIEW_GROUP_RELOAD_TARGETS.CURRENT_GROUP) {
			this.reloadCurrentGroup();
			return;
		}
		if (target === VIEW_GROUP_RELOAD_TARGETS.AVAILABLE_GROUPS) {
			this.getAllGroupForList();
		}
	}

	private syncIdentity(): void {
		this.roster.replaceIdentity({
			group: this.group,
			groupContext: this.groupContext,
			user: this.user,
			isAdmin: this.isAdmin,
			isHead: this.isHead,
			isHeadOfGroup: this.isHeadOfGroup,
			isDeputy: this.isDeputy
		});
	}

	private deleteMember(request: ViewGroupMemberDeletionRequest): void {
		this.roster.deleteMember(request.userId, request.groupId)
			.takeUntil(this.destroy$)
			.subscribe(
				response => this.ui.completeMemberDeletion(response),
				() => undefined
			);
	}

}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/views/view-group/view-group.component.ts